<template>
    <c-modal :value="value" title="Manage Tags" :scrollable="false" @closed="onClose">
        <div class="selected-tags-box mb-2" :class="{ 'is-empty': !selectedTags.length }">
            <span v-if="!selectedTags.length" class="placeholder">No tags added</span>
            <c-badge
                v-for="tag in selectedTags"
                :key="tag.id"
                pill
                color="purple"
                class="mr-1 d-flex align-items-center"
            >
                {{ tag.name }}
                <b-icon icon="x" class="ml-1 selected-tag-remove-icon" @click="removeTag(tag)" />
            </c-badge>
        </div>

        <div class="tags-count mb-2">{{ selectedTagIds.length }}/{{ effectiveMaxTags }} Tags</div>

        <c-input v-model="search" class="mb-2" placeholder="Type to search or create a tag" @enter="onEnter" />

        <button v-if="showCreateButton" type="button" class="create-tag-btn mb-2" @click="createTag">
            + Create tag for "{{ trimmedSearch }}"
        </button>

        <div v-if="filteredTags.length" class="suggested-tags-box">
            <button
                v-for="tag in filteredTags"
                :key="tag.id"
                type="button"
                class="suggested-tag-btn"
                :disabled="isMaxReached"
                @click="selectTag(tag)"
            >
                + {{ tag.name }}
            </button>
        </div>

        <template #modal-footer>
            <div class="d-flex justify-content-between w-100">
                <c-button text="Close" variant="secondary" @click="onClose" />
                <c-button text="Save" variant="primary" @click="onSave" />
            </div>
        </template>
    </c-modal>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import CModal from '@/components/CModal.vue';
import CButton from '@/components/shared/CButton';
import CBadge from '@/components/shared/CBadge';
import CInput from '@/components/shared/CInput';
import type { Tag } from '@/api/typings/tags';

// Must match TagsBL.MaxTagsPerProduct — the real limit is enforced server-side in
// ProductBL.AssignTags, this default only covers the brief window before the caller has the
// server-provided value to pass down via the maxTags prop.
const props = withDefaults(
    defineProps<{
        value: boolean;
        productId: string;
        tags: Tag[];
        availableTags: Tag[];
        maxTags?: number;
    }>(),
    {
        productId: '',
        maxTags: 5,
        tags: () => [],
        availableTags: () => []
    }
);

const effectiveMaxTags = computed(() => props.maxTags);

const emit = defineEmits<{
    (event: 'close'): void;
    (event: 'save', payload: { productId: string; tagIds: number[]; newTagNames: string[] }): void;
}>();

// A selected tag is either a real, already-existing Tag (number id) or a not-yet-created one
// represented by a placeholder whose id IS its name. selectedTagIds is the single source of truth
// for both, so tagIds/newTagNames can always be derived from it at save time instead of tracked
// separately.
type NewTagOption = { id: string; name: string };
type SelectedTag = Tag | NewTagOption;

const selectedTagIds = ref<Array<number | string>>([]);
const search = ref('');

watch(
    () => props.value,
    (isOpen) => {
        if (isOpen) {
            selectedTagIds.value = props.tags.map((tag) => tag.id);
            search.value = '';
        }
    }
);

const trimmedSearch = computed(() => search.value.trim());
const isMaxReached = computed(() => selectedTagIds.value.length >= effectiveMaxTags.value);

const selectedTags = computed<SelectedTag[]>(() =>
    selectedTagIds.value.map((id) => {
        if (typeof id === 'string') {
            return { id, name: id };
        }
        // Prefer the product's own tag data (always current) over the once-loaded availableTags
        // cache, which can be missing a tag assigned elsewhere (e.g. by another session) and would
        // otherwise fall back to displaying the raw id instead of its name.
        return (
            props.tags.find((tag) => tag.id === id) ??
            props.availableTags.find((tag) => tag.id === id) ?? { id, name: String(id) }
        );
    })
);

// Tags matching the typed text, excluding ones already selected — shown as clickable chips only
// while the user is actively typing (empty search shows nothing, per design).
const filteredTags = computed<Tag[]>(() => {
    const term = trimmedSearch.value.toLowerCase();
    if (!term) {
        return [];
    }
    return props.availableTags.filter(
        (tag) => tag.name.toLowerCase().includes(term) && !selectedTagIds.value.includes(tag.id)
    );
});

// Checked against both real tags AND not-yet-created ones already in the selection, so typing the
// same new tag name twice can't push a second, duplicate placeholder into selectedTagIds.
const hasExactMatch = computed(() => {
    const term = trimmedSearch.value.toLowerCase();
    return (
        props.availableTags.some((tag) => tag.name.toLowerCase() === term) ||
        selectedTagIds.value.some((id) => typeof id === 'string' && id.toLowerCase() === term)
    );
});

const showCreateButton = computed(() => trimmedSearch.value.length > 0 && !hasExactMatch.value && !isMaxReached.value);

const removeTag = (tag: SelectedTag) => {
    selectedTagIds.value = selectedTagIds.value.filter((id) => id !== tag.id);
};

const addToSelection = (id: number | string) => {
    if (isMaxReached.value) {
        return;
    }
    selectedTagIds.value = selectedTagIds.value.concat(id);
    search.value = '';
};

const selectTag = (tag: Tag) => addToSelection(tag.id);

const createTag = () => {
    if (!trimmedSearch.value) {
        return;
    }
    addToSelection(trimmedSearch.value);
};

// Enter mirrors clicking the top suggestion (if any match is showing) or the "Create tag" button,
// so keyboard-only use isn't limited to Tab-ing to each chip button.
const onEnter = () => {
    if (filteredTags.value.length) {
        selectTag(filteredTags.value[0]);
    } else if (showCreateButton.value) {
        createTag();
    }
};

const onClose = () => emit('close');

const onSave = () => {
    const tagIds = selectedTagIds.value.filter((id): id is number => typeof id === 'number');
    const newTagNames = selectedTagIds.value.filter((id): id is string => typeof id === 'string');
    emit('save', { productId: props.productId, tagIds, newTagNames });
};
</script>

<style lang="scss" scoped>
@import 'styles/utilities/functions';

.tags-count {
    font-size: 0.8rem;
    color: #666;
}

.selected-tags-box {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.25rem;
    min-height: 2.5rem;
    padding: 0.5rem;
    background: get-color('gray-lightest');
    border: 1px solid get-color('gray-200');
    border-radius: 0.25rem;

    &.is-empty {
        border-style: dashed;
    }
}

.placeholder {
    font-size: 0.875rem;
    color: get-color('gray-500');
}

.selected-tag-remove-icon {
    cursor: pointer;
    margin-left: 0.4rem !important;
    font-size: 0.8rem !important;
}

.create-tag-btn {
    display: block;
    width: 100%;
    text-align: left;
    border: 1px dashed get-color('gray-400');
    border-radius: 0.25rem;
    background: transparent;
    color: get-color('gray-500');
    font-size: 0.8rem;
    padding: 0.4rem 0.6rem;
}

.suggested-tags-box {
    display: flex;
    flex-wrap: wrap;
    gap: 0.35rem;
}

.suggested-tag-btn {
    border: 1px dashed get-color('purple-light');
    border-radius: 999px;
    background: transparent;
    color: get-color('purple');
    font-size: 0.8rem;
    padding: 0.25rem 0.6rem;

    &:hover:not(:disabled) {
        background: get-color('purple-lightest');
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
}

.c-badge-wrapper {
    padding: 0.4rem 0.65rem !important;
    font-size: 0.7rem !important;
    font-weight: normal !important;
    border: 1px solid get-color('purple') !important;
    background: get-color('purple-lightest') !important;
}
</style>
